import { ButtonGroup, Flex, HStack, Link, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { RiExternalLinkLine } from "@remixicon/react";
import capitalize from "lodash/capitalize";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import FormSwitch from "@/common/components/Form/FormSwitch";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useSlugParam } from "@/common/hooks/useParam";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import LinearConnectionGuard from "@/integration/components/linear/LinearConnectionGuard";
import { LinearTrackingEntityAutocompleteSelect } from "@/integration/components/linear/LinearTrackingEntityAutocompleteSelect";
import { useFlag } from "@reflag/react-sdk";
import { LinearTrackingEntitySchema } from "@reflag/shared/linearConnectionAPI";
import { z } from "@reflag/shared/utils/zod";

const FormSchema = z.object({
  trackingEntity: LinearTrackingEntitySchema.nullable(),
  targetingRulesChangeNotification: z.boolean(),
  feedbackNotification: z.boolean(),
});

const defaultValues = {
  trackingEntity: null,
  targetingRulesChangeNotification: true,
  feedbackNotification: false,
} satisfies z.infer<typeof FormSchema>;

type FormType = z.infer<typeof FormSchema>;

export function SettingsLinear() {
  const { org } = useEnvironment();
  const { featureId } = useSlugParam("feature");
  const { data: feature } = useFeatureData(featureId!);
  const form = useForm<FormType>({
    resolver: zodResolver(FormSchema),
    mode: "onChange",
    defaultValues: feature?.integrations.linear || defaultValues,
  });

  const updateMutation = useFeatureUpdateMutation(featureId!);
  const submitHandler = useFormMutationSubmitHandler(
    form,
    updateMutation,
    (result) => {
      form.reset(result.integrations.linear ?? defaultValues);
    },
    {
      prepareVariables: (data) => ({
        id: featureId!,
        integrations: {
          linear: data.trackingEntity
            ? {
                ...data,
                trackingEntity: data.trackingEntity,
              }
            : null,
        },
      }),
    },
  );

  return (
    <Flex direction="column" gap={4} maxW="mediumForm">
      <Restricted alertStyle="callout" feature={feature} role="contributor">
        <LinearConnectionGuard>
          <FormProvider {...form}>
            <Flex
              as="form"
              direction={"column"}
              gap={4}
              onSubmit={submitHandler}
            >
              <FormLinearIntegration />
              <FormRootError />
              {org.integrations.linear && (
                <ButtonGroup>
                  <FormSubmit showPostSubmit />
                  <FormReset />
                </ButtonGroup>
              )}
            </Flex>
          </FormProvider>
        </LinearConnectionGuard>
      </Restricted>
    </Flex>
  );
}

function FormLinearIntegration() {
  const { isEnabled: linearIntegrationFeedbackEnabled } = useFlag(
    "linear-integration-feedback",
  );

  const {
    watch,
    formState: { isSubmitting },
  } = useFormContext<FormType>();
  const trackingEntity = watch("trackingEntity");
  const { env, org } = useEnvironment();

  const isDisabled =
    isSubmitting || !env.isProduction || !org.integrations.linear;

  const what = !trackingEntity
    ? "an update"
    : trackingEntity?.type === "issue"
      ? "a comment"
      : "a project update";

  return (
    <Flex direction="column" gap={4}>
      <ManagedFormControl
        isDisabled={!org.integrations.linear}
        label="Linear issue/project associated with this flag"
        name="trackingEntity"
        render={({ field }) => (
          <LinearTrackingEntityAutocompleteSelect
            {...field}
            canClear
            onChange={field.onChange}
          />
        )}
      />
      {!!trackingEntity?.url && (
        <Link
          color="dimmed"
          href={trackingEntity.url}
          target="_blank"
          textDecoration="none"
          width={"fit-content"}
        >
          <HStack>
            <Text fontSize="sm">Open {trackingEntity.type} in Linear</Text>
            <RiExternalLinkLine size={16} />
          </HStack>
        </Link>
      )}
      {linearIntegrationFeedbackEnabled && (
        <Flex direction="column" w="full">
          <FormSwitch
            isDisabled={isDisabled || !trackingEntity}
            label={`Post ${what} when feedback is submitted`}
            name="feedbackNotification"
          />
          <Text color="dimmed" fontSize="sm" maxW="xs">
            {capitalize(what)} posted when a user submits feedback for this
            feature flag.
          </Text>
        </Flex>
      )}
    </Flex>
  );
}
